import { useMutation } from '@apollo/client';
import { Close, HelpOutline } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  ListItemIcon,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Tooltip
} from '@mui/material';
import { Course } from '@thro-types/core';

import axios from 'axios';
import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import MUTATION from 'gql/mutations/ModulesService/deleteParticipantConfiguration.gql';

import { useCoursesQuery } from 'api/hooks/useCoursesQuery';
import { CardControlButtons } from 'app/components';
import { PerformantTextField } from 'app/components/PerformantTextField/PerformantTextField';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { SubjectType } from 'app/containers/AWPM-FWPM/ModuleSelections/models';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { sortCourses } from 'utils/courses';

import { MoreDetails } from './components/MoreDetail';

interface Props {
  index: number;
  participantConfig: any;
  remove: (index: number) => void;
}

export enum SemesterEnum {
  B1 = 'B1',
  B2 = 'B2',
  B3 = 'B3',
  B4 = 'B4',
  B5 = 'B5',
  B6 = 'B6',
  B7 = 'B7',
  B8 = 'B8',
  B9 = 'B9',
  M1 = 'M1',
  M2 = 'M2',
  M3 = 'M3'
}

export function ParticipantConfiguration({ index, participantConfig, remove }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { handleChange, values, setFieldValue } = useFormikContext<{
    id?: number;
    subjectType: SubjectType;
    maxParticipants: number;
    participantsConfigurations: {
      courseOfStudyIds: number[];
      admissionCriteria: any[];
      seats: number;
      hasAutomaticSeatDistribution: boolean;
      isGlobal: boolean | undefined;
      excludeCourses: boolean | undefined;
      excludedCourseOfStudyIds: number[];
    }[];
    isDefault: boolean;
  }>();

  const [infoOpen, setInfoOpen] = useState(false);
  const [showMore, setShowMore] = useState(true);
  const [globalEnabled, setGlobalEnabled] = useState(
    values.participantsConfigurations[index].isGlobal &&
      !values.participantsConfigurations[index].excludeCourses
  );
  const [excludeEnabled, setExcludeEnabled] = useState(
    values.participantsConfigurations[index].excludeCourses
  );

  const {
    data: courses,
    isError,
    error,
    isFetched
  } = useCoursesQuery({ params: { ordered: true } });

  useEffect(() => {
    if (isError && axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        dispatch(
          AlertActions.error(`${t(translations.pages.modules.alerts.failedLoadingCourseOfStudy())}`)
        );
      } else {
        dispatch(
          AlertActions.error(
            `${t(translations.pages.modules.alerts.failedLoadingCourseOfStudy())}: ${error.message}`
          )
        );
      }
    }
  }, [dispatch, isError, navigate, t, error]);

  const handleShowMore = () => setShowMore(old => !old);
  const [deleteParticipantConfiguration] = useMutation(MUTATION, {
    context: { clientName: 'federation' }
  });

  const handleDeleteParConfig = async () => {
    let toCheck: any = { ...values.participantsConfigurations[index] };
    if (Number(toCheck.id) >= 0) {
      let { errors } = await deleteParticipantConfiguration({
        variables: {
          id: Number(toCheck.id)
        }
      });
      if (errors) {
        dispatch(AlertActions.error(t(translations.pages.modules.alerts.deleteParConfFailed())));
      } else {
        dispatch(
          AlertActions.success(t(translations.pages.modules.alerts.deleteParConfSuccessful()))
        );
      }
    }
    remove(index);
  };

  const CourseOfStudySelect = ({
    courses,
    id,
    label,
    value
  }: {
    courses: Course[];
    id: string;
    label: string;
    value: number[];
  }) => {
    const sortedCourses = sortCourses(courses.slice());
    return (
      <Grid>
        {sortedCourses.length === 0 ? (
          <NoItems />
        ) : (
          <FormControl fullWidth variant="outlined" required>
            <TextField
              select
              id={id}
              name={id}
              value={value}
              onChange={handleChange}
              fullWidth
              slotProps={{
                select: {
                  multiple: true,
                  MenuProps: {
                    PaperProps: {
                      style: {
                        maxHeight: 300
                      }
                    },
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left'
                    },
                    transformOrigin: {
                      vertical: 'top',
                      horizontal: 'left'
                    }
                  },
                  renderValue: value => {
                    const sorted: Course[] = [];
                    for (let item of value as number[]) {
                      const a = sortedCourses.find(course => course.id === item);
                      a && sorted.push(a);
                    }

                    return sorted.map((course, index) => (
                      <Tooltip title={course?.name || 'name'} key={index + '-tooltip'}>
                        <Chip key={index + '-chip'} label={course?.shorthand} />
                      </Tooltip>
                    ));
                  }
                }
              }}
              required={!!values.id && !values.isDefault}
              label={t(label)}
            >
              {sortedCourses.map((c: Course, index) => {
                return (
                  <MenuItem id={`${c.id}`} key={index} value={c.id}>
                    {'(' + c.shorthand + ') ' + c.name}
                  </MenuItem>
                );
              })}
            </TextField>
          </FormControl>
        )}
      </Grid>
    );
  };

  const SeatInfoButton = () => (
    <Grid item onClick={() => setInfoOpen(v => !v)}>
      <MenuItem className="cardControlButtons__container">
        <Grid className="cardControlButtons__container__icon" item xs={3}>
          <ListItemIcon>
            <HelpOutline
              style={{
                width: 'auto',
                fontSize: '1.8em'
              }}
            />
          </ListItemIcon>
        </Grid>
      </MenuItem>
    </Grid>
  );

  return (
    <Grid container item xs={12} component={Paper} elevation={3} style={{ paddingBottom: '2em' }}>
      <Grid container justifyContent="flex-end">
        <CardControlButtons
          handleDelete={handleDeleteParConfig}
          handleShowMore={handleShowMore}
          showMore={showMore}
          index={index}
        >
          <SeatInfoButton />
        </CardControlButtons>
        <Grid item xs={12}>
          <Collapse in={infoOpen}>
            <Alert
              severity="info"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setInfoOpen(false);
                  }}
                >
                  <Close fontSize="inherit" />
                </IconButton>
              }
              style={{ marginBottom: 10 }}
            >
              <AlertTitle>
                {t(translations.pages.modules.explanationOfSeatDistributionTitle())}
              </AlertTitle>
              {t(translations.pages.modules.explanationOfSeatDistributionText())}
            </Alert>
          </Collapse>
        </Grid>
      </Grid>
      <Grid container justifyContent="space-around">
        <Grid item xs={11}>
          <Grid container gap={2}>
            <Grid item xs={12}>
              <FormControl
                className="moduleModal__body__input"
                required={!!values.id && !values.isDefault}
                fullWidth
              >
                {!isFetched ? (
                  <CircularProgress />
                ) : (
                  <Grid container gap={2}>
                    {values.subjectType === SubjectType.Awpm &&
                      (!!values.participantsConfigurations?.[index]?.isGlobal ||
                        !values.participantsConfigurations?.find?.(c => !!c.isGlobal)) && (
                        <Grid item>
                          <FormControlLabel
                            control={
                              <Switch
                                color="primary"
                                name={`participantsConfigurations[${index}].isGlobal`}
                                checked={values.participantsConfigurations?.[index]?.isGlobal}
                                onChange={e => {
                                  setFieldValue(
                                    `participantsConfigurations[${index}].isGlobal`,
                                    e.target.checked
                                  );
                                  setFieldValue(`participantsConfigurations[${index}].seats`, null);
                                  setGlobalEnabled(e.target.checked);
                                }}
                              />
                            }
                            disabled={excludeEnabled}
                            label={t(
                              translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.isGlobal()
                            )}
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                color="primary"
                                name={`participantsConfigurations[${index}].excludeCourses`}
                                checked={values.participantsConfigurations?.[index]?.excludeCourses}
                                onChange={e => {
                                  setFieldValue(
                                    `participantsConfigurations[${index}].excludeCourses`,
                                    e.target.checked
                                  );
                                  setGlobalEnabled(e.target.checked);
                                  setExcludeEnabled(e.target.checked);
                                }}
                              />
                            }
                            disabled={globalEnabled && !excludeEnabled}
                            label={t(
                              translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.excludeCourses()
                            )}
                          />
                        </Grid>
                      )}
                    <>
                      <Grid container spacing={2}>
                        <Grid item xs={10}>
                          {!!values.participantsConfigurations[index].isGlobal || excludeEnabled ? (
                            <TextField
                              id={`participantsConfigurations.${index}.courseOfStudyIsGlobal`}
                              disabled
                              fullWidth
                              label={t(
                                translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.courseOfStudyIsGlobal()
                              )}
                            ></TextField>
                          ) : (
                            <CourseOfStudySelect
                              courses={courses}
                              id={`participantsConfigurations.[${index}].courseOfStudyIds`}
                              label={translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.courseOfStudy()}
                              value={values?.participantsConfigurations[index]?.courseOfStudyIds}
                            />
                          )}
                        </Grid>
                        <Grid item xs={2}>
                          <PerformantTextField
                            name={`participantsConfigurations[${index}].seats`}
                            type="number"
                            InputProps={{
                              inputProps: { min: 0 }
                            }}
                            onChange={handleChange}
                            disabled={values.participantsConfigurations[index].isGlobal}
                            fullWidth
                            label={t(
                              translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.seats()
                            )}
                            value={values.participantsConfigurations[index].seats}
                          />
                        </Grid>
                      </Grid>
                      {!!values.participantsConfigurations[index].excludeCourses ? (
                        <Grid item xs={10}>
                          <CourseOfStudySelect
                            courses={courses}
                            id={`participantsConfigurations.${index}.excludedCourseOfStudyIds`}
                            label={translations.pages.modules.create.prioritizationAndApproval.participantConfiguration.chooseCoursestoExclude()}
                            value={
                              values?.participantsConfigurations[index]?.excludedCourseOfStudyIds ||
                              []
                            }
                          />
                        </Grid>
                      ) : (
                        <></>
                      )}
                    </>
                  </Grid>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              {showMore && <MoreDetails index={index} participantConfig={participantConfig} />}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
